import {
  Button,
  Checkbox,
  Flex,
  Group,
  Modal,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { type ChartTypeRegistry } from "chart.js";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  type ILogbookCharts,
  setChartsData,
} from "src/redux/slices/entriesGeneratorSlice";

interface Props {
  opened: boolean;
  close: () => void;
  data: ILogbookCharts[];
  setData: Dispatch<SetStateAction<ILogbookCharts[]>>;
}

const CreateChartModal = ({ opened, close, data, setData }: Props) => {
  const dispatch = useDispatch();
  const { logbooks } = useSelector(state => state.entriesGeneratorSlice);

  const handleSelect = (index: number, chartIndex: number, value: string) => {
    const temp = [...data];
    temp[index].data[chartIndex].type = value as keyof ChartTypeRegistry;
    temp[index].data[chartIndex].trendline = false;
    setData(temp);
  };

  const handleCheckbox = (
    index: number,
    chartIndex: number,
    checked: boolean,
  ) => {
    const temp = [...data];
    temp[index].data[chartIndex].trendline = checked;
    setData(temp);
  };

  const handleCreate = () => {
    dispatch(setChartsData(data));
  };

  const renderModalTitle = useCallback(
    () => (
      <Flex gap={8}>
        <Text size="lg" fw="bold">
          Charts
        </Text>
        <Tooltip
          w={200}
          multiline
          transitionProps={{ duration: 200 }}
          label="Below you can set the charts for those fields which contained some values">
          <IconInfoCircle style={{ cursor: "pointer" }} />
        </Tooltip>
      </Flex>
    ),
    [],
  );

  return (
    <Modal size="md" title={renderModalTitle()} opened={opened} onClose={close}>
      {data.length === 0 && (
        <Text>Provided fields do not contain any values</Text>
      )}
      {data.length > 0 &&
        data.map((el: ILogbookCharts, index) => (
          <div key={el.lbid}>
            <Text mb={12}>
              {logbooks.find(log => log.value === el.lbid)?.label}
            </Text>
            <div style={{ marginLeft: 20 }}>
              {el.data.map((chart, chartIndex) => (
                <Flex mb={20} direction="column" key={chart.name}>
                  <Text mb={8}>{chart.name}</Text>
                  <Flex align="center" gap={12}>
                    <Select
                      withinPortal
                      data={[
                        { label: "Line", value: "line" },
                        { label: "Bar", value: "bar" },
                        { label: "Pie", value: "pie" },
                      ]}
                      value={chart.type}
                      onChange={(value: string) =>
                        handleSelect(index, chartIndex, value)
                      }
                    />
                    <Checkbox
                      disabled={chart.type !== "bar" && chart.type !== "line"}
                      checked={chart.trendline}
                      onChange={event =>
                        handleCheckbox(
                          index,
                          chartIndex,
                          event.currentTarget.checked,
                        )
                      }
                      label="Trendline"
                    />
                  </Flex>
                </Flex>
              ))}
            </div>
          </div>
        ))}
      <Group justify="right" mt={10}>
        <Button onClick={handleCreate} disabled={data.length === 0}>
          Create
        </Button>
      </Group>
    </Modal>
  );
};

export default CreateChartModal;
